import _ from "lodash";
import { PROMO_TYPES, PROMO_TARGETS } from "./promoCode";
import promoCodesApi from "../../../api/promoCodesApi";
import {PRODUCT_CATEGORIES} from "../../../constants";

/**
 * Calculate the shipping price of an order from the given information.
 * @param {LineItem[]} lineItems
 * @param {Object} country
 * @param {Object} shippingMethod
 * @returns {Number}
 */
export function calcShippingPrice(lineItems, country, shippingMethod) {
  let result = 0;

  if (_.isEmpty(country) || _.isEmpty(shippingMethod))
    return 0;

  lineItems.forEach(lineItem => {
    if (lineItem.product.category === PRODUCT_CATEGORIES.SCALE)
      result += country.shippingPrices[shippingMethod.type] * lineItem.quantity;
  });

  return result;
}

/**
 * Calculate the sales tax of an order from the given information.
 * @param {LineItem[]} lineItems
 * @param {String} stateText
 * @param {Object} country
 * @param {Array} salesTaxRates
 * @param {PromoCode} promoCode
 * @returns {Number}
 */
export function calcSalesTax(lineItems, stateText, country, salesTaxRates, promoCode) {
  const scaleLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.SCALE);

  if (!scaleLineItem || _.isEmpty(stateText) || _.isEmpty(country) || country.code !== "US")
    return 0;

  let scalePrice = 0;

  lineItems.forEach(lineItem => {
    if (lineItem.product.category === PRODUCT_CATEGORIES.SCALE)
      scalePrice += lineItem.price;
  })

  let finalDevicePrice = scalePrice;

  const formattedStateText = stateText.toUpperCase();
  let appliedSalesTaxObj = null;

  for (let i = 0; i < salesTaxRates.length; i += 1) {
    const salesTaxRate = salesTaxRates[i];
    if (salesTaxRate.stateAbbrev === formattedStateText || salesTaxRate.stateName === formattedStateText) {
      appliedSalesTaxObj = salesTaxRate;
      break;
    }
  }

  // If we didn't find a state that has a sales tax, return 0.
  if (appliedSalesTaxObj === null)
    return 0;

  // If there is no promo code, we don't have to go any further.
  if (_.isEmpty(promoCode))
    return appliedSalesTaxObj.amount * finalDevicePrice;

  // If there is a promo code that discounts against the scale, we should only tax the discounted price - not the full scale price.
  if (promoCode.promoType === PROMO_TYPES.FLAT)
    // Note that no matter what the target is, we are considering the entire flat discount to be applied to the device.
    finalDevicePrice = scalePrice - promoCode.amount;
  else if (promoCode.promoType === PROMO_TYPES.PERCENTAGE) {
    // We should only consider the promo code against the sales tax if the promo code target affects the device price.
    // There are some promo code targets that don't affect the device price (ex. MEMBERSHIP and SHIPPING).
    if (promoCode.promoTarget === PROMO_TARGETS.TOTAL || promoCode.promoTarget === PROMO_TARGETS.DEVICE)
      finalDevicePrice = scalePrice - (scalePrice * promoCode.amount);
  }

  // If the scale price is free, we could actually have a negative finalDevicePrice. Make sure this doesn't happen.
  return Math.max(finalDevicePrice, 0) * appliedSalesTaxObj.amount;
}

/**
 * Calculate the promo code discount for an order from the given information.
 * @param {LineItem[]} lineItems
 * @param {PromoCode} promoCode
 * @param {Object} shippingMethod
 * @param {Object} country
 * @returns {Number}
 */
export function calcPromoDiscount(lineItems, promoCode, shippingMethod, country) {
  if (_.isEmpty(promoCode))
    return 0;

  let shippingPrice = calcShippingPrice(lineItems, country, shippingMethod);
  let scalePrice = 0;
  let planPrice = 0;
  let subtotal = 0;

  lineItems.forEach(lineItem => {
    if (lineItem.product.category === PRODUCT_CATEGORIES.SCALE)
      scalePrice += lineItem.price;
    else if (lineItem.product.category === PRODUCT_CATEGORIES.MEMBERSHIP)
      planPrice += lineItem.price;

    subtotal += lineItem.price;
  });

  let promoDiscount = 0;

  // A flat promo code means we're subtracting a static amount (ex. $10).
  if (promoCode.promoType === PROMO_TYPES.FLAT)
    promoDiscount = promoCode.amount;
  // A percentage promo code means we're taking off a percentage of the total price.
  else if (promoCode.promoType === PROMO_TYPES.PERCENTAGE) {
    // By default, apply the promo code to the total order price - shipping price. This corresponds to the 'TOTAL_WITHOUT_SHIPPING' promo target.
    let applyTo = subtotal;

    // A promo code can be applied to certain aspects of the order price (membership, shipping, etc).
    if (promoCode.promoTarget === PROMO_TARGETS.TOTAL)
      applyTo = subtotal + shippingPrice;
    else if (promoCode.promoTarget === PROMO_TARGETS.DEVICE)
      applyTo = scalePrice;
    else if (promoCode.promoTarget === PROMO_TARGETS.MEMBERSHIP)
      applyTo = planPrice;
    else if (promoCode.promoTarget === PROMO_TARGETS.SHIPPING)
      applyTo = shippingPrice;

    promoDiscount = applyTo * promoCode.amount;
  }

  return promoDiscount;
}

/**
 * Given order data, calculate the total price of the order.
 * @param {LineItem[]} lineItems
 * @param {Object} country
 * @param {String} state
 * @param {Object} shippingMethod
 * @param {Array} salesTaxRates
 * @param {PromoCode} promoCode
 * @returns {Number}
 */
export function calcTotalPrice(lineItems, country, state, shippingMethod, salesTaxRates, promoCode) {
  let result = 0;

  lineItems.forEach(lineItem => { result += lineItem.price });

  result += calcShippingPrice(lineItems, country, shippingMethod);

  result += calcSalesTax(lineItems, state, country, salesTaxRates, promoCode);

  result -= calcPromoDiscount(lineItems, promoCode, shippingMethod, country);

  // As a safeguard, don't let the total price become negative. This could potentially happen with a cheap product (ex. $5) and a promo code (ex. -$10).
  return Math.max(result, 0);
}

/**
 * Given promo code and plan data, fetch promo code details.
 * @param {String} promoCodeText
 * @param {String} purchaseFlow
 * @param {Object[]} lineItems
 * @param {String} [referralCode] - Must be specified if attempting to apply the promo for using a referral code.
 * @returns {Promise}
 */
export function applyPromoCode(promoCodeText, purchaseFlow, lineItems, referralCode) {
  return new Promise((resolve, reject) => {
    if (_.isEmpty(promoCodeText))
      return;

    promoCodesApi.search(promoCodeText, lineItems, purchaseFlow, referralCode)
      .then(validPromoCode => resolve(validPromoCode))
      .catch(error => reject(error));
  });
}

/**
 * Calculate the promo code discount percentage for an order from the given information.
 * @param {Number} promoDiscount
 * @param {Number} totalPrice
 * @returns {Number}
 */
export function calcPromoPercentage(promoDiscount, totalPrice) {
  return Math.round((100 * promoDiscount) / totalPrice);
}
